/**
 * CommitteeDecidePage — one-time scoped decision page for committee members.
 *
 * Committee_Email_Scoped_Access_28Sep2026.md §5:
 * - No sidebar, no login, no navigation to anything else.
 * - Token from URL path → GET /public/committee-invites/{token}
 * - Shows requirement summary + comparative statement table.
 * - If no decision yet: shows a decision form.
 * - If already decided: shows read-only view of who decided, when, which vendor.
 * - Expired / revoked: shows appropriate message, never an error page.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

// iter_450 follow-up §3: display all timestamps in IST using canonical formatter
import { formatDateTime as _fmtDT } from '../lib/format';
function formatIST(iso) {
  if (!iso) return '—';
  return _fmtDT(iso) + ' IST';
}

async function fetchInvite(token) {
  const res = await fetch(`${API}/api/public/committee-invites/${encodeURIComponent(token)}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

async function submitDecision(token, selected_vendor, rationale) {
  const res = await fetch(`${API}/api/public/committee-invites/${encodeURIComponent(token)}/decide`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ selected_vendor, rationale }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

export default function CommitteeDecidePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [rationale, setRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [decided, setDecided] = useState(null);  // successful decision result

  useEffect(() => {
    if (!token) return;
    fetchInvite(token)
      .then(d => {
        setData(d);
        if (d.committee_decision) {
          setDecided({ existing: true, ...d.committee_decision });
        }
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedVendor) { alert('Please select a vendor.'); return; }
    if (!rationale.trim()) { alert('Please provide a rationale.'); return; }
    if (!window.confirm(`Confirm your selection: ${selectedVendor}`)) return;
    setSubmitting(true);
    try {
      const res = await submitDecision(token, selectedVendor, rationale.trim());
      if (res.already_decided) {
        setDecided({ existing: true, ...res.committee_decision });
      } else {
        setDecided({ selected_vendor_name: selectedVendor, decided_by: res.decided_by, new: true });
      }
    } catch (ex) {
      setError(ex.message);
    } finally {
      setSubmitting(false);
    }
  };

  const nav = (
    <nav className="bg-[#3b4872] text-white px-6 py-3 flex items-center justify-between" data-testid="committee-nav">
      <div className="font-bold text-lg tracking-tight">Ellora EPC — Vendor Selection</div>
      <div className="text-xs text-slate-300">Secure · Single-purpose · One RFQ only</div>
    </nav>
  );

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {nav}
      <div className="flex items-center justify-center flex-1 text-slate-400 text-sm">Loading…</div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {nav}
      <div className="max-w-lg mx-auto mt-16 bg-white border border-red-200 rounded-sm p-6 text-center" data-testid="committee-error">
        <div className="text-red-600 font-semibold text-base mb-2">This link is no longer valid</div>
        <p className="text-sm text-slate-600">{error}</p>
        <p className="text-xs text-slate-400 mt-4">If you need to record a decision, ask Procurement to resend the invite.</p>
      </div>
    </div>
  );

  const rfq = data?.rfq || {};
  const comparative = data?.comparative || [];
  const member = data?.member || {};

  // Already decided — show read-only
  if (decided) {
    const d = decided;
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        {nav}
        <div className="max-w-2xl mx-auto mt-12 bg-white border border-slate-200 rounded-sm p-6" data-testid="committee-decided-view">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold">✓</div>
            <h2 className="font-bold text-lg text-slate-800">{d.new ? 'Decision recorded' : 'Decision already recorded'}</h2>
          </div>
          {d.existing && !d.new && (
            <p className="text-sm text-slate-600 mb-4">Another committee member has already recorded a vendor selection for this RFQ. Your link is now read-only.</p>
          )}
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 space-y-2 text-sm">
            <div><span className="font-semibold text-slate-600">Selected vendor:</span> <span className="font-bold text-[#3b4872]">{d.selected_vendor_name || d.selected_vendor}</span></div>
            <div><span className="font-semibold text-slate-600">Decided by:</span> {d.decided_by || member.name}</div>
            {d.decided_at && <div><span className="font-semibold text-slate-600">Date:</span> {formatIST(d.decided_at)}</div>}
            {d.rationale && <div><span className="font-semibold text-slate-600">Rationale:</span> {d.rationale}</div>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="committee-decide-page">
      {nav}
      <div className="max-w-3xl mx-auto w-full px-4 py-8 space-y-6">
        {/* Member greeting */}
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <p className="text-sm text-slate-600">
            Hello <strong>{member.name}</strong>{member.org ? ` (${member.org})` : ''} — you've been asked to select a vendor for the following requirement.
          </p>
          {data.expires_at && (
            <p className="text-xs text-slate-400 mt-1">
              This link expires on {formatIST(data.expires_at)}.
            </p>
          )}
        </div>

        {/* Requirement summary */}
        <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="committee-rfq-summary">
          <h2 className="font-display font-bold text-base text-[#3b4872] mb-3">Requirement — {rfq.rfq_no}</h2>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div><dt className="text-slate-500 text-xs font-semibold uppercase tracking-wide">Project</dt><dd className="font-medium">{rfq.project_name || '—'}</dd></div>
            <div><dt className="text-slate-500 text-xs font-semibold uppercase tracking-wide">Material / Service</dt><dd className="font-medium">{rfq.material_description || '—'}</dd></div>
            <div><dt className="text-slate-500 text-xs font-semibold uppercase tracking-wide">Quantity</dt><dd>{rfq.quantity} {rfq.unit}</dd></div>
            <div><dt className="text-slate-500 text-xs font-semibold uppercase tracking-wide">Required By</dt><dd>{rfq.required_by || '—'}</dd></div>
            {rfq.justification && (
              <div className="col-span-2"><dt className="text-slate-500 text-xs font-semibold uppercase tracking-wide">Justification</dt><dd className="text-slate-700">{rfq.justification}</dd></div>
            )}
          </dl>
        </div>

        {/* Comparative statement */}
        <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="committee-comparative">
          <h2 className="font-display font-bold text-base text-[#3b4872] mb-3">Comparative Statement</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-[#3b4872] text-white">
                  <th className="text-left px-3 py-2 text-xs">Rank</th>
                  <th className="text-left px-3 py-2 text-xs">Vendor</th>
                  <th className="text-right px-3 py-2 text-xs">Total Value</th>
                  <th className="text-center px-3 py-2 text-xs">Delivery</th>
                  <th className="text-left px-3 py-2 text-xs">Payment Terms</th>
                </tr>
              </thead>
              <tbody>
                {comparative.map((q, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-slate-50' : 'bg-white'} data-testid={`cs-row-${q.rank}`}>
                    <td className="px-3 py-2 font-bold text-[#3b4872]">{q.rank}</td>
                    <td className="px-3 py-2">{q.vendor_name || q.vendor || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">Rs.{Number(q.total_value || q.total || 0).toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-center">{q.delivery_days ? `${q.delivery_days} days` : '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{q.payment_terms || '—'}</td>
                  </tr>
                ))}
                {comparative.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400">No quotes available</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Decision form */}
        {comparative.length > 0 && (
          <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-sm p-5 space-y-4" data-testid="committee-decision-form">
            <h2 className="font-display font-bold text-base text-[#3b4872]">Your Decision</h2>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Select vendor *</label>
              <select value={selectedVendor} onChange={e => setSelectedVendor(e.target.value)}
                className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm" required
                data-testid="committee-vendor-select">
                <option value="">— Choose a vendor —</option>
                {comparative.map((q, i) => (
                  <option key={i} value={q.vendor_name || q.vendor}>
                    {q.rank}: {q.vendor_name || q.vendor} — Rs.{Number(q.total_value || q.total || 0).toLocaleString('en-IN')}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Rationale *</label>
              <textarea value={rationale} onChange={e => setRationale(e.target.value)}
                placeholder="State the reason for this vendor selection (quality, price, track record, etc.)"
                rows={4} required minLength={10}
                className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm"
                data-testid="committee-rationale" />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="committee-form-error">{error}</p>}
            <button type="submit" disabled={submitting || !selectedVendor || !rationale.trim()}
              className="w-full bg-[#3b4872] hover:bg-[#2c3658] text-white py-3 rounded-sm font-semibold text-sm disabled:opacity-50 transition-colors"
              data-testid="committee-submit-btn">
              {submitting ? 'Recording decision…' : 'Record my decision →'}
            </button>
            <p className="text-xs text-slate-400">
              Your decision is final and will be recorded under your name. Once submitted, no changes can be made through this link.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
